import { appConfigs } from '@/common/configs/app.config';
import { rialToToman } from '@/common/utils/money-exchange.utility';

export interface ProductPricingInput {
  price: number | null | undefined;
  discount: number | null | undefined;
  oldPrice?: number | null;
  finalPrice?: number | null;
  discountPercent?: number | null;
}

export interface ProductPricing {
  price: number;
  originalPrice?: number;
  discount?: number;
}

function toValidAmount(value: number | null | undefined) {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value
    : undefined;
}

function toDiscountRate(value: number | null | undefined) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 && value <= 1
    ? value
    : 0;
}

export function mapProductPricing({
  price: rawPrice,
  discount: rawDiscount,
  oldPrice: rawOldPrice,
  finalPrice: rawFinalPrice,
  discountPercent: rawDiscountPercent,
}: ProductPricingInput): ProductPricing {
  const basePrice = toValidAmount(rawPrice) ?? 0;
  const originalPriceInRial = toValidAmount(rawOldPrice) ?? basePrice;
  const finalPriceInRial =
    toValidAmount(rawFinalPrice) ??
    basePrice * (1 - toDiscountRate(rawDiscount));
  const discountRate = toDiscountRate(rawDiscount);
  const suppliedDiscountPercent =
    typeof rawDiscountPercent === 'number' &&
    Number.isFinite(rawDiscountPercent) &&
    rawDiscountPercent > 0 &&
    rawDiscountPercent <= 100;
  const discountPercent = suppliedDiscountPercent
    ? Math.round(rawDiscountPercent)
    : toValidAmount(rawFinalPrice) !== undefined && originalPriceInRial > 0
      ? Math.round(
          ((originalPriceInRial - finalPriceInRial) * 100) /
            originalPriceInRial
        )
      : Math.round(discountRate * 100);
  const hasDiscount =
    discountPercent > 0 && originalPriceInRial > finalPriceInRial;

  return {
    price: rialToToman(finalPriceInRial, appConfigs.rialExchangeRate),
    ...(hasDiscount
      ? {
          originalPrice: rialToToman(
            originalPriceInRial,
            appConfigs.rialExchangeRate
          ),
          discount: discountPercent,
        }
      : {}),
  };
}
